import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText, RotateCw, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { useData } from "@/context/DataContext";
import { CertificateTypeIcon } from "@/components/CertificateTypeIcon";
import { ExpiryMeter } from "@/components/ExpiryMeter";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge, Button, Card } from "@/components/ui";
import { daysBetween, formatDate, relativeTime } from "@/lib/format";
import { cn } from "@/lib/cn";

const timelineLabels = {
  requested: "Solicitud creada",
  docs_verified: "Documentos verificados",
  csr_generated: "CSR firmada",
  issued: "Certificado emitido",
  downloaded: "Descargaste tu PFX",
  renewed: "Renovación iniciada",
  revoked: "Certificado venció/revocado",
} as const;

export function CertificateDetail() {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useT();
  const { data } = useData();
  const navigate = useNavigate();

  const cert = data.certificates.find((c) => c.id === id);

  if (!cert) {
    return (
      <div className="py-20 text-center">
        <h2 className="text-xl font-semibold">Certificado no encontrado</h2>
        <Link to="/certificados" className="mt-6 btn-secondary inline-flex">
          {t("cert.back_to_list")}
        </Link>
      </div>
    );
  }

  const remaining = daysBetween(cert.expiresAt);
  const validityDays = cert.validityYears * 365;
  const events = data.timelines[cert.id] ?? [];
  const docs = data.documents.slice(0, 2);

  const handleDownload = () => {
    toast.success("Comenzando descarga…", { description: "Tu archivo .pfx estará listo en segundos." });
  };

  const handleRenew = () => {
    navigate("/solicitar");
  };

  return (
    <div className="space-y-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("cert.back_to_list")}
      </button>

      <Card className="relative overflow-hidden">
        <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-start sm:gap-8 sm:p-8">
          <CertificateTypeIcon type={cert.type} size="lg" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={cert.status} />
              <Badge tone="neutral">{cert.issuer}</Badge>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {t(`type.${cert.type}` as const)}
            </h1>
            <p className="text-sm text-slate-600">
              {t("cert.holder")}: <span className="font-medium text-slate-900">{cert.holder}</span>
              <span className="mx-2 text-slate-300">·</span>
              {cert.documentNumber}
            </p>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.issued_on")}</dt>
                <dd className="font-medium text-slate-900">{formatDate(cert.issuedAt, locale)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.expires_on")}</dt>
                <dd className="font-medium text-slate-900">{formatDate(cert.expiresAt, locale)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500">{t("cert.serial")}</dt>
                <dd className="font-mono text-xs text-slate-900">{cert.serial}</dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-col items-center gap-3 sm:items-end">
            <ExpiryMeter daysRemaining={remaining} validityDays={validityDays} size={110} strokeWidth={10} />
            <p
              className={cn(
                "text-xs font-medium",
                remaining < 0 ? "text-red-600" : remaining < 90 ? "text-amber-700" : "text-emerald-700"
              )}
            >
              {remaining < 0
                ? t("cert.expired_ago", { days: Math.abs(remaining) })
                : t("cert.days_remaining", { days: remaining })}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200/80 bg-slate-50/60 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleDownload} disabled={cert.status !== "active" && cert.status !== "expiring"}>
              <Download className="h-4 w-4" />
              {t("cert.download_pfx")}
            </Button>
            <Button variant="secondary" onClick={handleDownload} disabled={cert.status !== "active" && cert.status !== "expiring"}>
              <FileText className="h-4 w-4" />
              {t("cert.download_cer")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={handleRenew}>
              <RotateCw className="h-4 w-4" />
              {t("cert.renew")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => toast.message("Esta acción está deshabilitada en el modo demo.")}
              className="text-red-700"
            >
              <ShieldOff className="h-4 w-4" />
              {t("cert.revoke")}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-[1.3fr_1fr]">
        <Card className="p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            {t("cert.timeline_title")}
          </h3>
          {events.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">Aún no hay eventos.</p>
          ) : (
            <ol className="mt-6 space-y-5">
              {events.map((ev, i) => (
                <li key={ev.id} className="relative flex gap-4">
                  <div className="flex flex-col items-center">
                    <span
                      className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold",
                        i === 0 ? "bg-brand-600 text-white shadow-pop" : "bg-slate-100 text-slate-500"
                      )}
                    >
                      {events.length - i}
                    </span>
                    {i < events.length - 1 && <span className="my-1 flex-1 w-px bg-slate-200" />}
                  </div>
                  <div className="flex-1 pb-1">
                    <p className="text-sm font-medium text-slate-900">{timelineLabels[ev.kind]}</p>
                    <p className="text-xs text-slate-600">{ev.summary}</p>
                    <p className="mt-1 text-xs text-slate-400">{relativeTime(ev.at, locale)}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
              {t("cert.details_title")}
            </h3>
            <dl className="mt-5 divide-y divide-slate-100">
              {[
                [t("cert.spec_validity"), `${cert.validityYears} ${cert.validityYears === 1 ? "año" : "años"}`],
                [t("cert.spec_format"), "PKCS#12 (.pfx)"],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between py-3">
                  <dt className="text-sm text-slate-500">{label}</dt>
                  <dd className="text-sm font-medium text-slate-900">{value}</dd>
                </div>
              ))}
            </dl>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
              {t("cert.docs_title")}
            </h3>
            {docs.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">{t("docs.empty_title")}</p>
            ) : (
              <ul className="mt-5 space-y-2">
                {docs.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-200/80 p-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">{d.name}</p>
                      <p className="text-xs text-slate-500">
                        {t("docs.uploaded_on")}: {formatDate(d.uploadedAt, locale)}
                      </p>
                    </div>
                    <button className="btn-ghost h-8 w-8 p-0" aria-label={t("common.download")}>
                      <Download className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
